import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService } from '../catalog/catalog.service';
import {
  CreatePriceListPayload,
  PriceListItemPayload,
  SalesApiService,
  UpdatePriceListPayload
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-price-list-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, SelectModule, TableModule, CurrencySelectorComponent,
    JalaliDateInputComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.priceLists.form.createTitle') : t('sales.priceLists.form.editTitle')" [description]="t('sales.priceLists.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/sales/price-lists']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>
        <section class="card grid">
          <label><span>{{ t('sales.priceLists.fields.code') }}</span><input pInputText [(ngModel)]="form.code" [readonly]="!isCreateMode()" /></label>
          <label><span>{{ t('sales.priceLists.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
          <label><span>{{ t('sales.priceLists.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
          <label><span>{{ t('sales.priceLists.fields.maxDiscount') }}</span><p-inputNumber [(ngModel)]="form.maxDiscountPercent" [min]="0" [max]="100" suffix="%" /></label>
          <label><span>{{ t('sales.priceLists.fields.minMargin') }}</span><p-inputNumber [(ngModel)]="form.minMarginPercent" [min]="0" [max]="100" suffix="%" /></label>
          <label><span>{{ t('sales.priceLists.fields.validFrom') }}</span><ain-date-input [(ngModel)]="form.validFromUtc" /></label>
          <label><span>{{ t('sales.priceLists.fields.validTo') }}</span><ain-date-input [(ngModel)]="form.validToUtc" /></label>
          @if (!isCreateMode()) {
            <label class="check"><input type="checkbox" [(ngModel)]="form.isActive" /><span>{{ t('sales.priceLists.active') }}</span></label>
          }
        </section>
        <section class="card">
          <div class="items-toolbar">
            <h3>{{ t('sales.priceLists.items.title') }}</h3>
            <button pButton type="button" [label]="t('sales.priceLists.items.add')" severity="secondary" (click)="addItem()"></button>
          </div>
          <p-table [value]="items()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('sales.priceLists.items.offering') }}</th>
                <th>{{ t('sales.priceLists.items.unitPrice') }}</th>
                <th>{{ t('sales.priceLists.items.minUnitPrice') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row let-i="rowIndex">
              <tr>
                <td>
                  <p-select [options]="offeringOptions()" optionLabel="label" optionValue="value" [(ngModel)]="row.offeringId" [filter]="true" filterBy="label" appendTo="body" />
                </td>
                <td><p-inputNumber [(ngModel)]="row.unitPrice" [min]="0" /></td>
                <td><p-inputNumber [(ngModel)]="row.minUnitPrice" [min]="0" /></td>
                <td><button pButton type="button" icon="pi pi-trash" severity="danger" text="true" (click)="removeItem(i)"></button></td>
              </tr>
            </ng-template>
          </p-table>
        </section>
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: space-between; margin-bottom: 10px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; margin-bottom: 10px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .items-toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
    .items-toolbar h3 { margin: 0; font-size: 15px; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesPriceListDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly catalog = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly items = signal<PriceListItemPayload[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);

  form = {
    code: '',
    name: '',
    currencyCode: 'IRR',
    maxDiscountPercent: 10 as number | null,
    minMarginPercent: null as number | null,
    validFromUtc: null as string | null,
    validToUtc: null as string | null,
    isActive: true
  };

  ngOnInit(): void {
    this.catalog.listOfferings().subscribe({
      next: rows => this.offerings.set(rows.filter(o => o.salesEnabled)),
      error: () => this.offerings.set([])
    });
    const id = this.route.snapshot.paramMap.get('priceListId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) {
      this.form.validFromUtc = new Date().toISOString();
      this.loading.set(false);
      return;
    }
    this.api.getPriceList(id!).subscribe({
      next: list => {
        this.form = {
          code: list.code,
          name: list.name,
          currencyCode: list.currencyCode,
          maxDiscountPercent: list.maxDiscountPercent ?? null,
          minMarginPercent: list.minMarginPercent ?? null,
          validFromUtc: list.validFromUtc,
          validToUtc: list.validToUtc ?? null,
          isActive: list.isActive
        };
        this.items.set((list.items ?? []).map(i => ({ offeringId: i.offeringId, unitPrice: i.unitPrice, minUnitPrice: i.minUnitPrice })));
        this.loading.set(false);
      },
      error: () => { this.errorMessage.set(this.t('sales.priceLists.errorLoad')); this.loading.set(false); }
    });
  }

  offeringOptions(): { label: string; value: string }[] {
    return this.offerings().map(o => ({ label: `${o.code} - ${o.title}`, value: o.id }));
  }

  addItem(): void { this.items.update(rows => [...rows, { offeringId: '', unitPrice: 0 }]); }
  removeItem(index: number): void { this.items.update(rows => rows.filter((_, i) => i !== index)); }

  save(): void {
    this.saving.set(true);
    const items = this.items().filter(i => i.offeringId);
    const validity = {
      validFromUtc: this.form.validFromUtc ?? undefined,
      validToUtc: this.form.validToUtc ?? undefined,
      maxDiscountPercent: this.form.maxDiscountPercent,
      minMarginPercent: this.form.minMarginPercent
    };
    if (this.isCreateMode()) {
      const { validFromUtc, validToUtc, isActive, ...header } = this.form;
      const payload: CreatePriceListPayload = { ...header, ...validity, items };
      this.api.createPriceList(payload).subscribe({
        next: list => void this.router.navigate(['/sales/price-lists', list.id]),
        error: () => this.failSave()
      });
      return;
    }
    const id = this.route.snapshot.paramMap.get('priceListId')!;
    const payload: UpdatePriceListPayload = {
      name: this.form.name,
      currencyCode: this.form.currencyCode,
      ...validity,
      isActive: this.form.isActive,
      items
    };
    this.api.updatePriceList(id, payload).subscribe({
      next: () => this.saving.set(false),
      error: () => this.failSave()
    });
  }

  private failSave(): void { this.errorMessage.set(this.t('sales.priceLists.errorSave')); this.saving.set(false); }
  t(key: string): string { return this.localization.translate(key); }
}
